/**
 * Pool/lane DI consistency repair (KOA-18258).
 *
 * Workflows edited under collab before the participant-bounds vocabulary existed were
 * persisted with lane bands that outgrew a pool frozen at its session seed — bpmn-js
 * renders the overhang as a phantom "extra lane" that cannot be deleted (it IS the one
 * lane). The repair is grow-only: expand the pool to the union of its lane bands (plus
 * the pool header strip on the title side) so every band tiles the pool body again.
 * Node positions are never touched, and a consistent diagram heals to null (idempotent).
 */

export interface IPoolLaneRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** The pool title band bpmn-js reserves between the pool edge and its lane bands. */
export const POOL_LANE_HEADER_W: number = 30;

/** Sub-pixel jitter from bpmn-js layout math must not count as corruption. */
const HEAL_EPSILON: number = 0.5;

/**
 * The grow-only pool bounds that cover every lane band (header strip included), or
 * null when the pool already covers them (nothing to heal). `isHorizontal` picks the
 * header side: a horizontal pool carries its title strip LEFT of the lanes, a vertical
 * one ABOVE them.
 */
export function healedPoolBounds(
  pool: IPoolLaneRect,
  lanes: readonly IPoolLaneRect[],
  isHorizontal: boolean = true
): IPoolLaneRect | null {
  if (lanes.length === 0) {
    return null;
  }
  let laneLeft: number = Number.POSITIVE_INFINITY;
  let laneTop: number = Number.POSITIVE_INFINITY;
  let laneRight: number = Number.NEGATIVE_INFINITY;
  let laneBottom: number = Number.NEGATIVE_INFINITY;
  for (const lane of lanes) {
    laneLeft = Math.min(laneLeft, lane.x);
    laneTop = Math.min(laneTop, lane.y);
    laneRight = Math.max(laneRight, lane.x + lane.width);
    laneBottom = Math.max(laneBottom, lane.y + lane.height);
  }
  if (isHorizontal) {
    laneLeft -= POOL_LANE_HEADER_W;
  } else {
    laneTop -= POOL_LANE_HEADER_W;
  }
  const x: number = Math.min(pool.x, laneLeft);
  const y: number = Math.min(pool.y, laneTop);
  const right: number = Math.max(pool.x + pool.width, laneRight);
  const bottom: number = Math.max(pool.y + pool.height, laneBottom);
  const healed: IPoolLaneRect = { x, y, width: right - x, height: bottom - y };
  const unchanged: boolean =
    Math.abs(healed.x - pool.x) < HEAL_EPSILON &&
    Math.abs(healed.y - pool.y) < HEAL_EPSILON &&
    Math.abs(healed.width - pool.width) < HEAL_EPSILON &&
    Math.abs(healed.height - pool.height) < HEAL_EPSILON;
  return unchanged ? null : healed;
}
